/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { useRef, useMemo } from 'preact/hooks';
import { memo } from 'preact/compat';
import { classNames } from '../utils/UNSAFE_classNames';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { SelectionMode, CurrentItemVariant, ListItemRendererContext } from '../UNSAFE_Collection';
import { FocusTrap } from '../UNSAFE_FocusTrap';
import { useId } from '../hooks/UNSAFE_useId';
import { Roles } from '../UNSAFE_ListView';
import { getListViewRoles } from './ListRoles';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { listItemStyles, listItemMultiVariantStyles } from './themes/ListStyles.css';
import { compareListItemContext } from '../utils/PRIVATE_collectionUtils';

export const ITEM_SELECTOR = '[data-oj-key]';

/**
 * Props for the ListViewItem Component
 */
export type Props<K, D> = {
  children: (context: ListItemRendererContext<K, D>) => ComponentChildren;
  context: ListItemRendererContext<K, D>;
  itemDepth?: number;
  isFocused: boolean;
  isFocusRingVisible: boolean;
  isActive: boolean;
  isGridlineVisible: boolean;
  isSelected: boolean;
  isTopGridlineVisible?: boolean;
  isTabbable?: boolean;
  role: Roles;
  selectionMode: SelectionMode;
  suggestion?: 'end' | true;
  currentItemVariant?: CurrentItemVariant;
};

/**
 * The internal component used to render a single item in ListView.
 */
export function ListViewItem<K extends string | number, D>({
  children,
  context,
  itemDepth,
  isFocused,
  isFocusRingVisible,
  isActive,
  isGridlineVisible,
  isSelected,
  isTopGridlineVisible = false,
  isTabbable = false,
  role,
  selectionMode,
  suggestion,
  currentItemVariant
}: Props<K, D>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const roles = useMemo(() => getListViewRoles(role), [role]);
  const itemKey = context.metadata.key;
  const itemIndex = context.index;

  // some roles come with related aria attributes which need to be set
  const cellRoleAttributes =
    roles.cell === 'option'
      ? {
          role: roles.cell,
          'aria-posinset': itemIndex + 1,
          'aria-setsize': -1
        }
      : {
          role: roles.cell
        };

  // don't bubble Arrow Up and Down events to parent when in isTabbable mode
  // this bubbling causes the outer CollectionFocus ring to change index while in the isTabbable mode.
  const handleKeyDown = (event: KeyboardEvent) => {
    if (isTabbable && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.stopPropagation();
    }
  };

  const { interactionProps, applyActiveStyle, applyHoverStyle, applyPseudoHoverStyle } =
    useInteractionStyle();

  const variantClasses = listItemMultiVariantStyles({
    selectable: isSelectable(selectionMode, isSelected) ? 'isSelectable' : 'notSelectable',
    selected: isSelected ? 'isSelected' : 'notSelected',
    selectedSingle:
      isSelected && selectionMode === 'single' ? 'isSelectedSingle' : 'notSelectedSingle',
    needsEventsHover: applyHoverStyle ? 'isNeedsEventsHover' : 'notNeedsEventsHover',
    pseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover',
    active: applyActiveStyle || isActive ? 'isActive' : 'notActive',
    focusHighlight:
      currentItemVariant === 'highlight' && isFocused ? 'isFocusHighlight' : 'notFocusHighlight',
    focusRingVisible:
      isFocusRingVisible && isFocused ? 'isFocusRingVisible' : 'notFocusRingVisible',
    gridlineTop: isTopGridlineVisible ? 'visible' : 'hidden',
    gridlineBottom:
      isGridlineVisible ||
      (suggestion === 'end' && !isGridlineVisible && (!isSelected || selectionMode !== 'single'))
        ? 'visible'
        : 'hidden'
  });
  const classes = classNames([variantClasses]);
  const styleInterpolations = mergeInterpolations<FlexitemProps>([
    ...Object.values(flexitemInterpolations)
  ]);
  const { class: cls, ...cellStyles } = styleInterpolations({
    flex: '1 1 auto',
    alignSelf: 'center'
  });
  const cellClasses = classNames([
    cls,
    selectionMode === 'multiple' && listItemStyles.checkboxContainer
  ]);

  // todo: use translated text for selector
  // note cannot put focus on gridcell div since JAWS will not read the aria-rowindex
  // correctly, so unfortunately needed another div
  return (
    <div
      aria-rowindex={role === 'grid' ? itemIndex + 1 : undefined}
      aria-level={itemDepth}
      data-oj-key={itemKey}
      class={classes}
      ref={rootRef}
      role={roles.item}
      {...(suggestion && { 'data-oj-suggestion': true })}
      {...(typeof itemKey === 'number' && { 'data-oj-key-type': 'number' })}
      {...interactionProps}>
      <div
        id={useId()}
        style={cellStyles}
        class={cellClasses}
        aria-posinset={role === 'grid' ? undefined : itemIndex + 1}
        aria-setsize={role === 'grid' ? undefined : -1}
        onKeyDown={handleKeyDown}
        aria-colindex={role === 'grid' ? 1 : undefined}
        aria-selected={selectionMode !== 'none' ? isSelected : undefined}
        {...cellRoleAttributes}>
        <TabbableModeContext.Provider value={{ isTabbable }}>
          <FocusTrap isDisabled={!isTabbable} restoreFocusRef={false}>
            {children(context)}
          </FocusTrap>
        </TabbableModeContext.Provider>
      </div>
    </div>
  );
}

/**
 * Helper method to determine whether item is selectable
 * @returns true if item is selectable (show hover effect), false otherwise
 */
const isSelectable = (selectionMode: SelectionMode, isItemSelected: boolean) => {
  return selectionMode !== 'none' && (selectionMode === 'multiple' || !isItemSelected);
};

export const MemoizeListViewItem = memo(ListViewItem, (prev: any, next: any) => {
  if (prev && next) {
    return (
      prev.children === next.children &&
      prev.isFocused === next.isFocused &&
      prev.isFocusRingVisible === next.isFocusRingVisible &&
      prev.isSelected === next.isSelected &&
      prev.isTabbable === next.isTabbable &&
      prev.isActive === next.isActive &&
      prev.suggestion === next.suggestion &&
      prev.isGridlineVisible === next.isGridlineVisible &&
      prev.isTopGridlineVisible === next.isTopGridlineVisible &&
      compareListItemContext(prev.context, next.context)
    );
  }
  return false;
});
